import type {TitleProps} from '@Panels/cancel-subscription-panel/cancel-subscription-panel.types';
import {updateStep} from '@Panels/cancel-subscription-panel/cancel-subscription-reducer';
import {cancelSubscription as cancelSubscriptionThunk} from '@Panels/cancel-subscription-panel/cancel-subscription-panel-thunk';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import {TransitionDirection} from '@Panels/transitionable-panel-v2/transitionable-panel-v2.types';
import {executeThunk} from '@Utils/thunk.util';
import {isIOSApp} from '@Utils/browser.util';
import {shuffleArray} from '@Utils/array.util';

export enum CancelSubscriptionPanelStep {
  FEATURE_USAGE = 'feature-usage',
  SURVEY = 'survey',
  PAUSE = 'pause',
  SWITCH_PLAN = 'switch-plan',
  SUPPORT_FORM = 'support-form',
  SUPPORT_CONFIRMATION = 'support-confirmation',
  CANCEL_CONFIRMATION = 'cancel-confirmation',
}

export enum CancelSurveyReasonIDs {
  WILL_SUBSCRIBE_AGAIN = 'will-subscribe-again',
  PRICE_TOO_HIGH = 'price-too-high',
  NOT_USING_ENOUGH = 'not-using-enough',
  TROUBLE_WITH_PMW_AI = 'trouble-with-pmw-ai',
  HAD_TROUBLE_USING_PMW = 'had-trouble-using-pmw',
  NOT_ENOUGH_DESIGN_FEATURES = 'not-enough-design-features',
  NOT_ENOUGH_TEMPLATE_VARIETY = 'not-enough-template-variety',
  DOESNT_HAVE_FEATURES = 'doesnt-have-features',
  DONT_UNDERSTAND_HOW_TO_USE = 'dont-understand-how-to-use',
  OTHER = 'other',
  NONE = 'none',
}

export enum RetentionOfferType {
  HALF_OFF_NEXT_MONTH = 'half-off-next-month',
  FREE_NEXT_MONTH = 'free-next-month',
}

export enum PauseIntervals {
  ONE_MONTH = 1,
  TWO_MONTHS = 2,
  THREE_MONTHS = 3,
  SIX_MONTHS = 6,
}

/**
 * The cancellation-survey reasons shown before "Other" (which is always pinned last), in a freshly shuffled order.
 * The order is computed once per dialog open and stored in Redux so navigating between steps (and back) does not
 * reshuffle the list within the same cancellation session.
 */
export function getShuffledCancelReasonIds(): CancelSurveyReasonIDs[] {
  return shuffleArray<CancelSurveyReasonIDs>([
    CancelSurveyReasonIDs.WILL_SUBSCRIBE_AGAIN,
    CancelSurveyReasonIDs.PRICE_TOO_HIGH,
    CancelSurveyReasonIDs.NOT_USING_ENOUGH,
    CancelSurveyReasonIDs.TROUBLE_WITH_PMW_AI,
    CancelSurveyReasonIDs.DOESNT_HAVE_FEATURES,
    CancelSurveyReasonIDs.HAD_TROUBLE_USING_PMW,
    CancelSurveyReasonIDs.DONT_UNDERSTAND_HOW_TO_USE,
  ]);
}

export function getFormattedDateForPauseInterval(interval: PauseIntervals, subscriptionExpiresOnTimestamp: number | null): string {
  const date = subscriptionExpiresOnTimestamp === null ? new Date() : new Date(subscriptionExpiresOnTimestamp * 1000);
  date.setMonth(date.getMonth() + interval.valueOf());
  return formatDate(date);
}

export function formatDate(date: Date): string {
  return date.toLocaleDateString('en-us', {month: 'long', day: 'numeric', year: 'numeric'});
}

export function getTitleForStep(step: CancelSubscriptionPanelStep, showPauseOnly: boolean, showDefaultFeatureUsages: boolean, cancelReason: CancelSurveyReasonIDs): TitleProps {
  let title = '',
    showBackButton = false;
  if (showPauseOnly || step === CancelSubscriptionPanelStep.PAUSE) {
    const isPlanningToReturn = !showPauseOnly && step === CancelSubscriptionPanelStep.PAUSE && cancelReason === CancelSurveyReasonIDs.WILL_SUBSCRIBE_AGAIN;
    title = window.i18next.t(isPlanningToReturn ? 'pmwjs_planning_to_return' : 'pmwjs_pause_your_subscription');
    showBackButton = showPauseOnly !== undefined && !showPauseOnly;
  }

  if (step === CancelSubscriptionPanelStep.FEATURE_USAGE) {
    title = window.i18next.t(showDefaultFeatureUsages ? 'pmwjs_are_you_sure_you_want_to_cancel' : 'pmwjs_wait_dont_let_your_creativity_end_now');
  }

  if (step === CancelSubscriptionPanelStep.SURVEY) {
    title = window.i18next.t('pmwjs_why_do_you_want_to_cancel');
    showBackButton = true;
  }

  if (step === CancelSubscriptionPanelStep.SWITCH_PLAN) {
    title = window.i18next.t('pmwjs_switch_to_a_cheaper_plan');
    showBackButton = true;
  }

  if (step === CancelSubscriptionPanelStep.SUPPORT_FORM) {
    title = window.i18next.t('pmwjs_tell_us_what_went_wrong');
    showBackButton = true;
  }

  if (step === CancelSubscriptionPanelStep.SUPPORT_CONFIRMATION) {
    title = window.i18next.t('pmwjs_support_thank_you_for_your_feedback');
  }

  if (step === CancelSubscriptionPanelStep.CANCEL_CONFIRMATION) {
    title = window.i18next.t('pmwjs_were_sorry_to_see_you_go');
  }

  return {
    text: title,
    showBackButton: showBackButton,
  };
}

export function goBackToPreviousStep(): void {
  const step = window.PMW.redux.store.getState().cancelSubscriptionPanel.currentStep;

  let previousStep = null;
  if (step === CancelSubscriptionPanelStep.SURVEY) {
    previousStep = CancelSubscriptionPanelStep.FEATURE_USAGE;
  } else if (step === CancelSubscriptionPanelStep.PAUSE || step === CancelSubscriptionPanelStep.SWITCH_PLAN || step === CancelSubscriptionPanelStep.SUPPORT_FORM) {
    previousStep = CancelSubscriptionPanelStep.SURVEY;
  }

  if (previousStep !== null) {
    window.PMW.redux.store.dispatch(
      updateStep({
        step: previousStep,
        direction: TransitionDirection.BACKWARD,
      })
    );
  }
}

export function closeCancelSubscriptionModal(): void {
  window.PMW.redux.store.dispatch(closeModal(MODALS_IDS.CANCEL_SUBCSRIPTION_MODAL));
}

export function onThunkError(): void {
  alert(window.i18next.t('pmwjs_error_message'));
}

export async function cancelSubscription(reason: CancelSurveyReasonIDs, comments: string): Promise<void> {
  if (isIOSApp()) {
    closeCancelSubscriptionModal();
    window.PMW.openIOSDeviceSubscriptions();
    return;
  }

  await executeThunk(
    () =>
      window.PMW.redux.store.dispatch(
        cancelSubscriptionThunk({
          reason,
          comments,
        })
      ),
    onCancelSubscriptionSuccess,
    onThunkError
  );
}

function onCancelSubscriptionSuccess(): void {
  window.PMW.gtm.trackGA4CustomEvent('CancelSubscription');
  window.PMW.redux.store.dispatch(
    updateStep({
      step: CancelSubscriptionPanelStep.CANCEL_CONFIRMATION,
      direction: TransitionDirection.FORWARD,
    })
  );
}

export function goToCanceledPage(): void {
  window.location.href = window.PMW.util.site_url('premium/canceled');
}
